import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { toast } from 'react-toastify';
import {
  FiArrowLeft, FiCheck, FiDownload, FiEye, FiPlus, FiTrash2, FiUploadCloud,
} from 'react-icons/fi';
import { getCompany } from '../../../Redux/selecters';
import LeaseApiService from '../../services/apiService';
import {
  FilePreview, IMAGE_RE, fileExt, formatSize, normalizeFile,
} from './procurementFiles';
import './procurementApproval.css';

const DOT_COLORS = {
  grn: '#2F7F9E', 'purchase order': '#6B4BB0', requisition: '#B07A1F', issue: '#64748B',
};
const DOC_TYPE_LABEL = {
  grn: 'Goods Receipt Note', 'purchase order': 'Purchase Order', requisition: 'Requisition', issue: 'Stock Issue',
};
const STATUS_PILL = {
  Approved: { bg: '#E4F3EB', fg: '#175F3E' },
  'On hold': { bg: '#FBF0D5', fg: '#7A5A0B' },
  Rejected: { bg: '#FBE7E5', fg: '#8E2A23' },
  'Awaiting decision': { bg: '#ECEAF3', fg: '#4A4760' },
};

const Attachments = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const apiService = useMemo(() => new LeaseApiService(), []);
  const companies = useSelector(getCompany);
  const row = state?.row;
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);

  const goBack = () => navigate('/Lease/Management/Procurement/Approval');

  useEffect(() => {
    if (!row) { setLoading(false); return; }
    (async () => {
      try {
        const params = new URLSearchParams();
        params.append('DocNo', row.docNo);
        params.append('DocType', String(row.docType).replace(/\s+/g, ''));
        const res = await apiService.getApiParamater('GET_PROCUREMENT_DOC_FILES', `?${params.toString()}`);
        const body = res?.data;
        const list = Array.isArray(body) ? body : body?.result || body?.data || [];
        setFiles(list.map(normalizeFile));
      } catch (error) {
        console.error('Error fetching attachments:', error);
        toast.error('Unable to load attachments.');
      } finally {
        setLoading(false);
      }
    })();
  }, [row, apiService]);

  if (!row) {
    return (
      <div className="proc-page">
        <button type="button" className="proc-back" onClick={goBack}><FiArrowLeft size={16} />Back to approvals</button>
        <p>Open a document&apos;s attachments from the approvals list.</p>
      </div>
    );
  }

  const typeKey = String(row.docType).toLowerCase();
  const status = row.isApproved ? 'Approved' : row.isHold ? 'On hold' : row.isRejected ? 'Rejected' : 'Awaiting decision';
  const pill = STATUS_PILL[status];
  const viewable = files.filter((f) => f.href);
  const entity = companies?.selectedCompany?.companyName || '—';
  const uploadedBy = files.find((f) => f.uploadedBy)?.uploadedBy || row.user || '—';

  const downloadAll = () => {
    viewable.forEach((f, i) => setTimeout(() => window.open(f.href, '_blank', 'noopener'), i * 250));
  };
  const notAvailable = () => toast.info('Upload and delete are not available yet.');

  return (
    <div className="proc-page">
      <div className="proc-att-head">
        <div className="proc-att-title">
          <button type="button" className="proc-back" onClick={goBack}><FiArrowLeft size={16} />Back to approvals</button>
          <h1>Attachments</h1>
          <div className="proc-att-sub">
            <span className="proc-docno">{row.docNo}</span>
            <i className="sep" />
            <span className="proc-type"><i className="sq" style={{ background: DOT_COLORS[typeKey] || '#64748B' }} />{row.docType}</span>
            <i className="sep" />
            <span className="proc-pill" style={{ background: pill.bg, color: pill.fg }}>
              {status === 'Approved' && <FiCheck size={12} />}{status}
            </span>
          </div>
        </div>
        <div className="proc-att-actions">
          <button type="button" className="proc-btn proc-btn-ghost" onClick={downloadAll} disabled={!viewable.length}><FiDownload size={16} />Download all</button>
          <button type="button" className="proc-btn proc-btn-primary" onClick={notAvailable}><FiPlus size={16} />Add attachment</button>
        </div>
      </div>

      <section className="proc-panel proc-meta" aria-label="Document details">
        <div className="proc-kv"><span className="proc-kv-label">Document no</span><span className="proc-kv-value proc-mono">{row.docNo}</span></div>
        <div className="proc-kv"><span className="proc-kv-label">Document type</span><span className="proc-kv-value">{DOC_TYPE_LABEL[typeKey] || row.docType}</span></div>
        <div className="proc-kv"><span className="proc-kv-label">Date</span><span className="proc-kv-value proc-mono">{row.docDate || '—'}</span></div>
        <div className="proc-kv"><span className="proc-kv-label">Amount</span><span className="proc-kv-value proc-mono">{(Number(row.amount) || 0).toFixed(3)}</span></div>
        <div className="proc-kv"><span className="proc-kv-label">Entity</span><span className="proc-kv-value">{entity}</span></div>
        <div className="proc-kv"><span className="proc-kv-label">Uploaded by</span><span className="proc-kv-value">{uploadedBy}</span></div>
      </section>

      <section className="proc-panel proc-list" aria-label="Attachment list">
        <div className="proc-list-head">
          <h2>Files <span className="proc-mono proc-count">({files.length})</span></h2>
          <span>Images and PDFs open in the viewer. Other files download.</span>
        </div>
        <div className="proc-table-wrap">
          <table className="proc-table" style={{ minWidth: 860 }}>
            <thead>
              <tr>
                <th scope="col" style={{ width: '44%' }}>File</th>
                <th scope="col">Type</th>
                <th scope="col" className="num">Size</th>
                <th scope="col">Uploaded</th>
                <th scope="col">Remarks</th>
                <th scope="col" className="num">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={6} className="proc-empty-cell">Loading…</td></tr>}
              {!loading && files.length === 0 && <tr><td colSpan={6} className="proc-empty-cell">No attachments found.</td></tr>}
              {files.map((f) => {
                const e = fileExt(f.name);
                const isPdf = e === 'PDF';
                const isImg = IMAGE_RE.test(f.href || f.name);
                return (
                  <tr key={f.key}>
                    <td>
                      <button type="button" className="proc-file" onClick={() => f.href && setPreview(f.key)}>
                        <span className={`proc-ext ${isPdf ? 'pdf' : 'img'}`} aria-hidden="true">{e}</span>
                        <span className="proc-file-text"><span className="proc-file-name">{f.name}</span></span>
                      </button>
                    </td>
                    <td className="proc-sub proc-nowrap">{isImg ? 'Image' : 'Document'}</td>
                    <td className="num proc-mono proc-nowrap">{formatSize(f.size)}</td>
                    <td className="proc-sub proc-nowrap">
                      {f.uploadedOn ? <span className="proc-mono">{moment(f.uploadedOn).format('DD/MM/YYYY HH:mm')}</span> : '—'}
                      {f.uploadedBy && <span className="proc-faint"> by {f.uploadedBy}</span>}
                    </td>
                    <td className="proc-sub">{f.remarks}</td>
                    <td>
                      <div className="proc-row-actions">
                        <button type="button" className="proc-view-btn" aria-label={`View ${f.name}`} disabled={!f.href} onClick={() => setPreview(f.key)}><FiEye size={16} />View</button>
                        <a className="proc-icon-btn" aria-label={`Download ${f.name}`} href={f.href} download={f.name} target="_blank" rel="noreferrer"><FiDownload size={16} /></a>
                        <button type="button" className="proc-icon-btn" aria-label={`Remove ${f.name}`} onClick={notAvailable}><FiTrash2 size={16} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="proc-drop-wrap">
          <div className="proc-drop">
            <FiUploadCloud size={20} />
            <span>Drop files here or</span>
            <button type="button" className="proc-btn proc-btn-ghost proc-btn-sm" onClick={notAvailable}>Browse</button>
            <span className="proc-faint">JPG, PNG or PDF up to 10 MB</span>
          </div>
        </div>
      </section>

      {preview !== null && (
        <FilePreview
          files={viewable}
          startKey={preview}
          onClose={() => setPreview(null)}
          doc={{
            docNo: row.docNo, docType: row.docType, docDate: row.docDate, amount: row.amount, status,
          }}
        />
      )}
    </div>
  );
};

export default Attachments;
